/**
 * Contact Page - Clinafy Institutional Website
 *
 * Contact form, WhatsApp, email, and support information.
 */

import React, { useState } from 'react';
import { useSEO, SEO_CONFIG } from '../../hooks/useSEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  alpha,
} from '@mui/material';

// Icons
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Components
import Navbar from '../../components/landing/Navbar';
import Footer from '../../components/landing/Footer';
import { landingColors, landingGradients, landingShadows } from '../../theme/landingTheme';
import { supabase } from '../../services/supabaseService';

// ============================================================================
// Data
// ============================================================================

const contactMethods = [
  {
    icon: <WhatsAppIcon sx={{ fontSize: 32 }} />,
    title: 'WhatsApp',
    value: '(41) 99611-1900',
    description: 'Resposta em minutos',
    action: 'Abrir WhatsApp',
    href: 'https://wa.me/5541996111900?text=Ola!%20Tenho%20interesse%20na%20Clinafy.',
    color: '#25D366',
  },
  {
    icon: <EmailIcon sx={{ fontSize: 32 }} />,
    title: 'Email',
    value: 'clinafy@gmail.com',
    description: 'Resposta em até 2h',
    action: 'Enviar Email',
    href: 'mailto:clinafy@gmail.com',
    color: landingColors.primary.main,
  },
  {
    icon: <PhoneIcon sx={{ fontSize: 32 }} />,
    title: 'Telefone',
    value: '(41) 99611-1900',
    description: 'Seg-Sex 9h-18h',
    action: 'Ligar Agora',
    href: 'tel:+5541996111900',
    color: landingColors.neutral.gray600,
  },
];

const subjectOptions = [
  { value: 'duvidas', label: 'Dúvidas sobre o sistema' },
  { value: 'suporte', label: 'Suporte técnico' },
  { value: 'comercial', label: 'Informações comerciais' },
  { value: 'parceria', label: 'Proposta de parceria' },
  { value: 'outro', label: 'Outro assunto' },
];

// ============================================================================
// Component
// ============================================================================

const ContactPage: React.FC = () => {
  // SEO - Dynamic meta tags
  useSEO(SEO_CONFIG.contato);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Save to Supabase leads table
      const { error: supabaseError } = await supabase
        .from('leads')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          subject: formData.subject,
          message: formData.message,
          source: 'contact_form',
          status: 'new',
          created_at: new Date().toISOString(),
        });

      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        throw supabaseError;
      }

      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: landingColors.background.default }}>
      <Navbar />

      <Box component="main" id="main-content">
      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.hero,
          pt: { xs: 8, md: 10 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h1"
            align="center"
            sx={{
              color: landingColors.neutral.white,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Estamos aqui para ajudar
          </Typography>
          <Typography
            align="center"
            sx={{
              color: alpha(landingColors.neutral.white, 0.9),
              fontSize: { xs: '1.1rem', md: '1.25rem' },
            }}
          >
            Dúvida, sugestão ou problema? Fale conosco por qualquer canal.
          </Typography>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* CONTACT METHODS */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 6, md: 8 }, mt: -4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {contactMethods.map((method, index) => (
              <Grid key={index} size={{ xs: 12, md: 4 }}>
                <Card
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: landingShadows.card,
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: landingColors.neutral.white,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: landingShadows.lg,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      bgcolor: alpha(method.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: method.color,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {method.icon}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {method.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: landingColors.neutral.gray900,
                      mb: 0.5,
                    }}
                  >
                    {method.value}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: landingColors.neutral.gray500,
                      mb: 3,
                    }}
                  >
                    {method.description}
                  </Typography>

                  <Button
                    variant="outlined"
                    href={method.href}
                    target={method.href.startsWith('http') ? '_blank' : undefined}
                    sx={{
                      borderColor: method.color,
                      color: method.color,
                      '&:hover': {
                        borderColor: method.color,
                        bgcolor: alpha(method.color, 0.04),
                      },
                    }}
                  >
                    {method.action}
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* CONTACT FORM */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Envie sua mensagem
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              mb: 6,
            }}
          >
            Preencha o formulário abaixo e retornaremos em até 2 horas úteis.
          </Typography>

          <Card
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              boxShadow: landingShadows.card,
            }}
          >
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="contact-name"
                    label="Nome completo"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    autoComplete="name"
                    inputProps={{ 'aria-label': 'Nome completo' }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="contact-email"
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    autoComplete="email"
                    inputProps={{ 'aria-label': 'Email' }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="contact-phone"
                    label="Telefone (opcional)"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="(41) 99999-9999"
                    autoComplete="tel"
                    inputProps={{ 'aria-label': 'Telefone' }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="contact-subject"
                    select
                    label="Assunto"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    SelectProps={{
                      MenuProps: { 'aria-label': 'Assunto' } as any,
                    }}
                  >
                    {subjectOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    id="contact-message"
                    label="Mensagem"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={5}
                    variant="outlined"
                    placeholder="Descreva sua dúvida, sugestão ou problema..."
                    inputProps={{ 'aria-label': 'Mensagem' }}
                  />
                </Grid>

                {error && (
                  <Grid size={12}>
                    <Alert severity="error">{error}</Alert>
                  </Grid>
                )}

                <Grid size={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    endIcon={<SendIcon />}
                    sx={{
                      bgcolor: landingColors.primary.main,
                      py: 1.5,
                      px: 4,
                      '&:hover': {
                        bgcolor: landingColors.primary.dark,
                      },
                    }}
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                  </Button>
                </Grid>

                <Grid size={12}>
                  <Typography
                    sx={{
                      color: landingColors.neutral.gray500,
                      fontSize: '0.85rem',
                    }}
                  >
                    Ao enviar, você concorda com nossa política de privacidade.
                    Não se preocupe, odiamos spam tanto quanto você.
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* SUPPORT HOURS */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Card
            sx={{
              p: 4,
              borderRadius: 3,
              bgcolor: '#FFFFFF',
              border: `1px solid ${landingColors.neutral.gray200}`,
              boxShadow: landingShadows.card,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              <AccessTimeIcon sx={{ color: landingColors.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: landingColors.neutral.gray900 }}>
                Horário de Atendimento
              </Typography>
            </Box>

            <Grid container spacing={2} justifyContent="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray900 }}>
                  Segunda a Sexta
                </Typography>
                <Typography sx={{ color: landingColors.neutral.gray600 }}>
                  9h às 18h
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray900 }}>
                  Sábado
                </Typography>
                <Typography sx={{ color: landingColors.neutral.gray600 }}>
                  9h às 13h
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray900 }}>
                  Suporte Enterprise
                </Typography>
                <Typography sx={{ color: landingColors.neutral.gray600 }}>
                  24/7
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ width: '100%' }}
        >
          Mensagem enviada com sucesso! Retornaremos em breve.
        </Alert>
      </Snackbar>

      </Box>

      <Footer />
    </Box>
  );
};

export default ContactPage;
